import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose, faMoneyBillWave, faReceipt, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { ButtonIcon } from "../../../components/Button/Button"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { ExitCheckResponse } from "../../../hooks/Costumer/useCheckCustomerExit"
import { useTranslation } from "react-i18next"

interface CustomerDebitPopupProps {
  data: ExitCheckResponse | null
  onClose: () => void
}

export const CustomerDebitPopup: React.FC<CustomerDebitPopupProps> = ({ data, onClose }) => {
  const { t } = useTranslation("customer")

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.2s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("exit_check.orders_with_debit")}
              icon={faReceipt}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.fluxo.customer.customerDebitPopup.button.onClose"
                />
              }
            />

            {!data?.customerFound || !data?.hasActiveDebit ? (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700 mt-2">
                  {t("customer_debit.no_active_debit")}
                </div>
              </Animate>
            ) : (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                <div className="space-y-4 mt-3">
                  <div className="rounded-lg p-4 border shadow-sm bg-red-50 border-red-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">{data.customerName}</h2>

                        <span className="text-sm text-gray-600">
                          {t("exit_check.customer_cpf")}: {data.cpf || "—"}
                        </span>
                      </div>

                      <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700 flex items-center gap-2">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {t("exit_check.with_active_debit")}
                      </span>
                    </div>

                    <div className="mt-3 text-red-700 font-semibold flex items-center gap-2">
                      <FontAwesomeIcon icon={faMoneyBillWave} />
                      {t("exit_check.total_open")}: {data.totalDebit.toMoney()}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-red-100 overflow-hidden">
                    <div className="px-4 py-3 bg-red-50 border-b border-red-100 font-semibold text-red-700 flex items-center gap-2">
                      <FontAwesomeIcon icon={faReceipt} />
                      {t("exit_check.orders_with_debit")}
                    </div>

                    <div className="divide-y divide-red-100 max-h-[300px] overflow-y-auto">
                      {data.activeDebitOrders.map((order) => (
                        <div
                          key={order.orderId}
                          className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                        >
                          <div>
                            <div className="font-semibold text-gray-800">
                              {t("exit_check.order_prefix")} #{order.orderId}
                            </div>

                            <div className="text-sm text-gray-500">
                              {order.date.toReadableDate()}{" "}
                              {order.tableNumber ? `• ${t("exit_check.table_prefix")} ${order.tableNumber}` : ""}
                            </div>
                            {order.description && <div className="text-xs text-gray-500 mt-1">{order.description}</div>}
                          </div>

                          <div className="text-sm font-bold text-red-700">{order.totalFinal.toMoney()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Animate>
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
